import { useHomepageModals } from '../Contexts/HomepageModalsContext';
import { useDiffitBooks } from '../Contexts/DiffitBooksContext';
import { usePacketBase } from '../Contexts/PacketBaseContext';
import {
  usePacketInput,
  LITERALLY_ANYTHING_ID,
  TEXT_OR_LINK_ID,
  TABS,
} from '../Contexts/PacketInputContext';
import { generatePacket, isYoutubeUrl, packetInputHasErrors } from '../Utils/GenerationUtils';
import { useNavigate } from 'react-router-dom';
import { Mixpanel } from '../../../services/Mixpanel';
import { useError } from '../../../contexts/ErrorContext';
import { isURL } from 'validator';

export const useGenerateResources = () => {
  const {
    setTextTooLongError,
    setPossibleBookMatch,
    setInputValidationError,
    setIsGenerating,
    setYoutubeTranscriptModal,
    setTooManyPacketsError,
  } = useHomepageModals();
  const { findPossibleBookMatchForTopic = () => null } = useDiffitBooks() || {};
  const { getBaseValues } = usePacketBase();
  const { getInputValues, setUrl } = usePacketInput();
  const { topic } = getInputValues();
  const navigate = useNavigate();
  const { showError } = useError();

  const handleGenerateResources = async (overrides = {}) => {
    const input = {
      ...getBaseValues(),
      ...getInputValues(),
      ...overrides,
    };
    // validate input
    const inputErrors = packetInputHasErrors(input);
    if (inputErrors) {
      setInputValidationError(inputErrors);
      return;
    }
    if (input.contentStandard) {
      if (input.topic) {
        input.topic = `Standard: ${input.contentStandard.description} Focus Specifically On: ${input.topic}`;
      } else {
        // If we don't have a topic, we can still generate a packet with the standard description
        input.topic = `Standard: ${input.contentStandard.description}`;
      }
    }
    if (!overrides.proceedFromBookMatch && input.inputType.id === LITERALLY_ANYTHING_ID) {
      const possibleBookMatch = findPossibleBookMatchForTopic(topic);
      if (possibleBookMatch) {
        setPossibleBookMatch(possibleBookMatch);
        return;
      }
    }
    const trimmedTopic = input.topic?.trim() || '';
    if (input.inputType.id === LITERALLY_ANYTHING_ID && isURL(trimmedTopic)) {
      // Intercept YouTube URLs — open transcript modal instead of generating
      if (isYoutubeUrl(trimmedTopic)) {
        Mixpanel.track('YouTube URL Detected', { url: trimmedTopic, source: 'literally_anything' });
        setYoutubeTranscriptModal({
          url: trimmedTopic,
          onTranscript: (transcript) => {
            // Re-run generation with the transcript text instead of the YouTube URL
            handleGenerateResources({ topic: transcript, proceedFromBookMatch: true });
          },
        });
        return;
      }
      input.url = trimmedTopic;
      input.topic = null;
      input.inputType = TABS.find((tab) => tab.id === TEXT_OR_LINK_ID);
      input.preserveOriginalText = true;
    }
    setIsGenerating({ isGenerating: true, customMessage: overrides.customMessage || null });
    const { error, packetUrl } = await generatePacket({
      ...input,
      navigate,
    });
    let trackingData = {
      inputType: input.inputType.id,
      readingLevel: input.readingLevel.name,
      language: input.language.name,
      genre: input.genre.server_value,
      orientation: input.orientation,
      languageStandards: input.languageStandards,
      contentStandard: input.contentStandard,
      packetUrl: packetUrl ? window.location.host + packetUrl : null,
      templateId: input.templateId,
    };
    if (input.inputType.id === 'tradebook') {
      trackingData.bookTitle = input.book.title;
      trackingData.bookAuthor = input.book.author;
      trackingData.startChapter = input.startChapter.value;
      trackingData.endChapter = input.endChapter.value;
      trackingData.bookChapterCount = input.book.chapters.length;
    }
    Mixpanel.track('Generate', {
      ...trackingData,
    });
    if (error) {
      setIsGenerating({ isGenerating: false });
      if (error.name === 'TextTooLongError') {
        setTextTooLongError(error);
        setUrl('');
        setIsGenerating({ isGenerating: false });
      } else if (error.name === 'YouTubeDisabledError') {
        setYoutubeTranscriptModal({
          url: input.url || input.topic,
          onTranscript: (transcript) => {
            handleGenerateResources({ topic: transcript, proceedFromBookMatch: true });
          },
        });
        setIsGenerating({ isGenerating: false });
      } else if (error.name === 'TooManyPacketsError') {
        setTooManyPacketsError(true);
        setIsGenerating({ isGenerating: false });
      } else {
        showError({
          message: error.user_error_message,
        });
        setIsGenerating({ isGenerating: false });
      }
    }
  };

  return { handleGenerateResources };
};
